import { webCoreTypes } from "@core/core-types.di";
import { FallbackError } from "@core/domain/errors/fallback.error";
import type { Nullable } from "@core/domain/types/nullable.type";
import type {
    FarmCreateBody,
    FarmDto,
    FarmsDto,
    FarmUpdateBody,
} from "@farm/data/dto/farm.dto";
import type { LayerTypeDto } from "@farm/data/dto/layer-type.dto";
import type { LayerCreateBody, LayerDto } from "@farm/data/dto/layer.dto";
import type {
    PlotCreateBody,
    PlotDto,
    PlotFileDto,
    PlotUpdateBody,
} from "@farm/data/dto/plot.dto";
import { FarmMapper } from "@farm/data/mappers/farm.mapper";
import { LayerTypeMapper } from "@farm/data/mappers/layer-type.mapper";
import { LayerMapper } from "@farm/data/mappers/layer.mapper";
import { PlotMapper } from "@farm/data/mappers/plot.mapper";
import type { FarmCreateError } from "@farm/domain/errors/farm-create.error";
import type { FarmUpdateError } from "@farm/domain/errors/farm-update.error";
import type { LayerCreateError } from "@farm/domain/errors/layer-create.error";
import type { PlotUpdateError } from "@farm/domain/errors/plot-update.error";
import type { PlotUploadFileError } from "@farm/domain/errors/plot-upload-file.error";
import type {
    Farm,
    FarmCreate,
    Farms,
    FarmUpdate,
} from "@farm/domain/models/farm.model";
import type { LayerType } from "@farm/domain/models/layer-type.model";
import type { Layer, LayerCreate } from "@farm/domain/models/layer.model";
import type {
    Plot,
    PlotFile,
    PlotFileUpload,
    PlotUpdate,
} from "@farm/domain/models/plot.model";
import { SATELLITE_SOURCE_CONFIG } from "@farm/domain/models/satellite-source.model";
import type { IotApiClient } from "@iot-platform/core";
import { HttpNoContentResponse } from "@iot-platform/core";
import { inject, injectable } from "inversify";
import type { ResultAsync } from "neverthrow";
import { errAsync, ok } from "neverthrow";

@injectable()
export class FarmDatasource {
    static readonly FARM_PATH = "/farms";
    static readonly PLOT_FILE_PATH = "/farms/:farmId/parcels/:plotId/document";

    constructor(
        @inject(webCoreTypes.infrastructure.IotApiClient)
        private readonly iotApiClient: IotApiClient,
        @inject(LayerTypeMapper)
        private readonly layerTypeMapper: LayerTypeMapper,
        @inject(LayerMapper)
        private readonly layerMapper: LayerMapper,
        @inject(FarmMapper)
        private readonly farmMapper: FarmMapper,
        @inject(PlotMapper)
        private readonly plotMapper: PlotMapper,
    ) {}

    /** GET /farms: returns a flat array without pagination */
    fetchAll(): ResultAsync<Farms, FallbackError> {
        return this.iotApiClient.http
            .get<FarmsDto>(FarmDatasource.FARM_PATH)
            .mapErr(() => new FallbackError())
            .map((response) => this.farmMapper.mapList(response.data));
    }

    fetchById(farmId: number): ResultAsync<Farm, FallbackError> {
        return this.iotApiClient.http
            .get<FarmDto>(`${FarmDatasource.FARM_PATH}/${farmId}`)
            .mapErr(() => new FallbackError())
            .andThen((response) =>
                this.farmMapper
                    .mapDetail(response.data)
                    .mapErr(() => new FallbackError()),
            );
    }

    create(createFarm: FarmCreate): ResultAsync<Farm, FarmCreateError> {
        const farmCreateBody: FarmCreateBody = {
            farm: {
                name: createFarm.name,
                enabled_satellites: createFarm.enabledSatellites,
                user_ids: createFarm.userIds,
            },
        };

        return this.iotApiClient.http
            .post<FarmDto>(FarmDatasource.FARM_PATH, farmCreateBody)
            .mapErr(() => new FallbackError())
            .andThen((response) => this.farmMapper.mapDetail(response.data));
    }

    update(
        farmId: number,
        updateFarm: FarmUpdate,
    ): ResultAsync<Farm, FarmUpdateError> {
        const farmUpdateBody: FarmUpdateBody = {
            farm: {
                name: updateFarm.name,
                enabled_satellites: updateFarm.enabledSatellites,
                user_ids: updateFarm.userIds,
            },
        };

        return this.iotApiClient.http
            .put<FarmDto>(
                `${FarmDatasource.FARM_PATH}/${farmId}`,
                farmUpdateBody,
            )
            .mapErr(() => new FallbackError())
            .andThen((response) => this.farmMapper.mapDetail(response.data));
    }

    delete(farmId: number): ResultAsync<true, FallbackError> {
        return this.iotApiClient.http
            .delete(`${FarmDatasource.FARM_PATH}/${farmId}`)
            .mapErr(() => new FallbackError())
            .map(() => true);
    }

    addPlot(
        farmId: number,
        feature: GeoJSON.Polygon,
    ): ResultAsync<Plot, FallbackError> {
        const body: PlotCreateBody = {
            parcel: { geo_json: JSON.stringify(feature) },
        };

        return this.iotApiClient.http
            .post<PlotDto>(
                `${FarmDatasource.FARM_PATH}/${farmId}/parcels`,
                body,
            )
            .mapErr(() => new FallbackError())
            .andThen((response) =>
                this.plotMapper
                    .mapPlot(response.data)
                    .mapErr(() => new FallbackError()),
            );
    }

    updatePlot(
        farmId: number,
        updatePlot: PlotUpdate,
    ): ResultAsync<Plot, PlotUpdateError> {
        const plotUpdateBody: PlotUpdateBody = {
            parcel: {
                name: updatePlot.name ?? null,
                station_ids: updatePlot.stationIds ?? [],
                clay_percentage: updatePlot.clayPercentage ?? null,
                silt_percentage: updatePlot.siltPercentage ?? null,
                sand_percentage: updatePlot.sandPercentage ?? null,
            },
        };

        return this.iotApiClient.http
            .put<PlotDto>(
                `${FarmDatasource.FARM_PATH}/${farmId}/parcels/${updatePlot.id}`,
                plotUpdateBody,
            )
            .mapErr(() => new FallbackError())
            .andThen((response) => this.plotMapper.mapPlot(response.data));
    }

    deletePlot(
        farmId: number,
        plotId: string,
    ): ResultAsync<true, FallbackError> {
        return this.iotApiClient.http
            .delete(`${FarmDatasource.FARM_PATH}/${farmId}/parcels/${plotId}`)
            .mapErr(() => new FallbackError())
            .map(() => true);
    }

    uploadFile(
        plotId: string,
        farmId: string,
        plotFile: PlotFileUpload,
    ): ResultAsync<PlotFile, PlotUploadFileError> {
        const formData = this.plotMapper.mapToUploadFileBody(plotFile);

        return this.iotApiClient.http
            .post<PlotFileDto>(
                FarmDatasource.PLOT_FILE_PATH.replace(
                    ":farmId",
                    farmId,
                ).replace(":plotId", plotId),
                formData,
            )
            .mapErr(() => new FallbackError() as PlotUploadFileError)
            .andThen((response) =>
                this.plotMapper
                    .mapFileResponse(response.data)
                    .mapErr((error) => error as PlotUploadFileError),
            );
    }

    deletePlotFile(
        farmId: string,
        plotId: string,
    ): ResultAsync<true, FallbackError> {
        return this.iotApiClient.http
            .delete(
                `${FarmDatasource.FARM_PATH}/${farmId}/parcels/${plotId}/document`,
            )
            .mapErr(() => new FallbackError())
            .map(() => true);
    }

    async fetchAllLayerTypes(sources: string[]): Promise<LayerType[]> {
        const validSources = sources.filter(
            (source) => source in SATELLITE_SOURCE_CONFIG,
        );

        const promises = validSources.map(async (source) => {
            const config = SATELLITE_SOURCE_CONFIG[source];
            if (!config) return [];

            const result = await this.iotApiClient.http
                .get<LayerTypeDto>(config.layerTypesPath)
                .mapErr(() => new FallbackError())
                .map((response) => this.layerTypeMapper.mapList(response.data));

            return result.match(
                (layerTypes) => layerTypes,
                (error) => {
                    console.error(
                        `Error fetching layer types for ${source}:`,
                        error,
                    );
                    return [] as LayerType[];
                },
            );
        });

        const results = await Promise.all(promises);
        return results.flat();
    }

    fetchLayer(
        farmId: string,
        plotId: string,
        createLayer: LayerCreate,
    ): ResultAsync<Nullable<Layer>, LayerCreateError> {
        const config = SATELLITE_SOURCE_CONFIG[createLayer.source];
        if (!config) {
            return errAsync(new FallbackError());
        }

        const layerCreateBody: LayerCreateBody = {
            [config.bodyKey]: {
                layer: createLayer.layer,
                weeks_ago: createLayer.weeksAgo,
            },
        };

        const path = config.layerPath
            .replace(":farmId", farmId)
            .replace(":plotId", plotId);

        return this.iotApiClient.http
            .post<LayerDto>(path, layerCreateBody)
            .mapErr(() => new FallbackError())
            .andThen((response) => {
                if (response.status === HttpNoContentResponse.CODE) {
                    return ok(null);
                }
                return this.layerMapper
                    .map(response.data)
                    .mapErr(() => new FallbackError());
            });
    }
}
